import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  CircularProgress,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Stack } from "@mui/system";

import { UseSatelliteDevices } from "../hooks/use-satellite-devices";
import AddDeviceRow from "./add-device-row";
import DeviceRowMenu from "./device-row-menu";
import ConfirmDialog from "components/shared/confirm-dialog";

interface SatelliteDevicesTableProps {
  satelliteDevices: UseSatelliteDevices;
}

const SatelliteDevicesTable = ({
  satelliteDevices,
}: SatelliteDevicesTableProps) => {
  const {
    devices,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    isAdding,
    isSubmitting,
    editingIndex,
    formValues,
    touched,
    canSubmit,
    hasActiveSearch,
    startAdding,
    cancelAdding,
    confirmAdding,
    startEditing,
    removeDevice,
    updateField,
    touchField,
  } = satelliteDevices;
  const theme = useTheme();
  const border = `1px solid ${theme.palette.grey[300]}`;
  const isEmpty = devices.length === 0 && !isAdding;
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);
  const deviceToRemove =
    removeIndex !== null ? devices[removeIndex] : undefined;

  const handleConfirmRemove = () => {
    if (removeIndex === null) {
      return;
    }
    removeDevice(removeIndex).finally(() => setRemoveIndex(null));
  };

  const renderFormRow = () => (
    <AddDeviceRow
      values={formValues}
      touched={touched}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      isEditing={editingIndex !== null}
      onFieldChange={updateField}
      onFieldBlur={touchField}
      onConfirm={confirmAdding}
      onCancel={cancelAdding}
    />
  );

  return (
    <TableContainer sx={{ mt: 3 }}>
      {isLoading && <LinearProgress data-testid="satellite-devices-loading" />}
      <Table sx={{ border }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>IMEI</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Model name</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Notes</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {isAdding && editingIndex === null && renderFormRow()}
          {devices.map((device, index) =>
            isAdding && editingIndex === index ? (
              <React.Fragment key={`${device.imei}-${index}`}>
                {renderFormRow()}
              </React.Fragment>
            ) : (
              <TableRow
                key={`${device.imei}-${index}`}
                data-testid="satellite-device-row"
              >
                <TableCell>{device.imei}</TableCell>
                <TableCell>{device.model}</TableCell>
                <TableCell>{device.notes}</TableCell>
                <TableCell align="right">
                  <DeviceRowMenu
                    onEdit={() => startEditing(index)}
                    onRemove={() => setRemoveIndex(index)}
                  />
                </TableCell>
              </TableRow>
            ),
          )}
          {isEmpty && (
            <TableRow>
              <TableCell colSpan={4} sx={{ borderBottom: "none" }}>
                <Stack alignItems="center" gap={1} sx={{ py: 6 }}>
                  {hasActiveSearch ? (
                    <>
                      <Typography variant="h6">No results found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        No satellite devices match your search.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6">
                        No satellite devices yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add device to company. Changes apply immediately.
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        type="button"
                        startIcon={<AddIcon />}
                        onClick={startAdding}
                        data-testid="satellite-empty-add-device-button"
                      >
                        Add Device
                      </Button>
                    </>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {hasMore && (
        <Stack alignItems="center" sx={{ py: 2 }}>
          {isLoadingMore ? (
            <CircularProgress
              size={20}
              data-testid="satellite-load-more-loader"
            />
          ) : (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={loadMore}
              data-testid="satellite-load-more"
            >
              Load more
            </Link>
          )}
        </Stack>
      )}
      <ConfirmDialog
        open={removeIndex !== null}
        title="Remove satellite device"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Remove"
        disabled={isSubmitting}
        disableCancelBtn={isSubmitting}
        disableCloseOnConfirm
        disableBackgroundClick
        setOpen={(isOpen) => {
          if (!isOpen && !isSubmitting) {
            setRemoveIndex(null);
          }
        }}
        onConfirm={handleConfirmRemove}
      >
        <Typography variant="body1">
          Remove satellite device
          {deviceToRemove ? ` "${deviceToRemove.imei}"` : " this device"}? Once
          you save, this action can&apos;t be undone.
        </Typography>
      </ConfirmDialog>
    </TableContainer>
  );
};

export default SatelliteDevicesTable;
